import { debounce } from 'lodash'
import React from 'react'

// A continuous resize (e.g. dragging the chart/table splitter) fires many
// ResizeObserver ticks per second with no natural coalescing — debounce the
// resulting `step` updates so the axis settles once instead of rapidly
// thinning/un-thinning labels on every tick.
const RESIZE_DEBOUNCE_MS = 100

interface UseYAxisLabelStepOptions {
  itemCount: number
  fontSizePx: number
  /** Maximum number of labels to show regardless of available height */
  maxLabels: number
  /**
   * Vertical chrome (chart margin top + bottom + x-axis height) that never
   * holds a category row — subtracted from the measured container height so
   * density is computed against the actual plot height, not the full SVG.
   */
  chromeHeightPx: number
}

interface UseYAxisLabelStepResult {
  containerRef: React.RefObject<HTMLDivElement>
  /** Pass as `interval={step - 1}` to a Recharts YAxis */
  step: number
}

/**
 * Pure helper — computes the label step (thinning factor) from the container
 * height and the per-call options. Hoisted to module scope so it isn't
 * re-created on every render. Exported for direct unit testing.
 */
export const computeLabelStep = (
  height: number,
  itemCount: number,
  fontSizePx: number,
  maxLabels: number,
  chromeHeightPx: number,
): number => {
  const plotHeight = Math.max(0, height - chromeHeightPx)
  // Matches CustomYAxisTick's own wrap-mode line spacing (AxisTickUtils.tsx),
  // not a paragraph-style line-height — the tick label is a raw SVG <text>.
  const labelHeight = fontSizePx + 2
  const densityMax = Math.max(1, Math.floor(plotHeight / labelHeight))
  const cappedMax = Math.min(maxLabels, densityMax)
  return Math.max(1, Math.ceil(itemCount / cappedMax))
}

/**
 * Computes the y-axis label interval step for horizontal bar charts.
 *
 * Uses a ResizeObserver to derive how many labels fit in the current height,
 * then caps that count at `maxLabels` so the axis never shows more than the
 * configured maximum — regardless of how much space is available.
 *
 * Returns `{ containerRef, step }`. Attach `containerRef` to the chart's
 * outer container element and pass `interval={step - 1}` to the Recharts
 * YAxis.
 */
export const useYAxisLabelStep = ({
  itemCount,
  fontSizePx,
  maxLabels,
  chromeHeightPx,
}: UseYAxisLabelStepOptions): UseYAxisLabelStepResult => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  // Belt-and-braces initial guess: assume we can fit at most maxLabels before
  // the ResizeObserver fires. Avoids the "interval=0 / show every label"
  // frame that would otherwise appear while the observer is still pending.
  const [step, setStep] = React.useState(() =>
    Math.max(1, Math.ceil(itemCount / maxLabels)),
  )

  // Debounced wrapper for ResizeObserver-driven updates only. The initial
  // synchronous measure below is NOT debounced — it must paint before the
  // first frame, per its own comment.
  const debouncedSetStepRef = React.useRef(
    debounce(setStep, RESIZE_DEBOUNCE_MS),
  )

  React.useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const debouncedSetStep = debouncedSetStepRef.current

    // Synchronous measure before the browser paints — keeps the first frame
    // thinned on chart remount (e.g. after a grouping-toggle refetch).
    setStep(
      computeLabelStep(
        el.getBoundingClientRect().height,
        itemCount,
        fontSizePx,
        maxLabels,
        chromeHeightPx,
      ),
    )

    const ro = new ResizeObserver(([entry]) => {
      if (!entry) return
      debouncedSetStep(
        computeLabelStep(
          entry.contentRect.height,
          itemCount,
          fontSizePx,
          maxLabels,
          chromeHeightPx,
        ),
      )
    })
    ro.observe(el)
    return () => {
      ro.disconnect()
      debouncedSetStep.cancel()
    }
  }, [fontSizePx, itemCount, maxLabels, chromeHeightPx])

  return { containerRef, step }
}
